/**
 * DV-TEMP-001: Event Filters Component
 * URL-driven filter controls for Events v2 timeline viewer
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Search, Calendar, Filter, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { TimelineFilters } from '@/types/execution-events';

// Flexible time range for UI state
interface FlexibleTimeRange {
  from?: string;
  to?: string;
}

// Flexible filters for UI (allows partial time range)
interface FlexibleTimelineFilters extends Omit<TimelineFilters, 'timeRange'> {
  timeRange?: FlexibleTimeRange;
}
import { Stage, type StageName } from '@/lib/shared-types';
import type { EventType } from '@/types/events-v2';

export interface EventFiltersProps {
  filters: FlexibleTimelineFilters;
  onFiltersChange: (filters: FlexibleTimelineFilters) => void;
  className?: string;
  'data-testid'?: string;
}

// Available filter options - derived from Zod schema
const STAGE_OPTIONS: { value: StageName; label: string }[] = Stage.options.map(stage => ({
  value: stage,
  label: stage.charAt(0).toUpperCase() + stage.slice(1)
}));

// Event type options with proper labels
const EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: 'stage_started', label: 'Stage Started' },
  { value: 'stage_completed', label: 'Stage Completed' },
  { value: 'stage_failed', label: 'Stage Failed' },
  { value: 'pipeline_started', label: 'Pipeline Started' },
  { value: 'pipeline_completed', label: 'Pipeline Completed' },
  { value: 'qc_started', label: 'QC Started' },
  { value: 'qc_completed', label: 'QC Completed' },
  { value: 'qc_failed', label: 'QC Failed' },
  { value: 'content_ready', label: 'Content Ready' },
  { value: 'scores_ready', label: 'Scores Ready' },
  { value: 'billing_committed', label: 'Billing Committed' },
  { value: 'billing_record_created', label: 'Billing Record' },
  { value: 'export_preview', label: 'Export Preview' },
  { value: 'error', label: 'Error' },
  { value: 'processing', label: 'Processing' },
  { value: 'quality_warning', label: 'Quality Warning' },
];

export function EventFilters({
  filters,
  onFiltersChange,
  className,
  'data-testid': testId = 'event-filters'
}: EventFiltersProps) {
  const [personaInput, setPersonaInput] = useState('');
  const [fromDateTime, setFromDateTime] = useState('');
  const [toDateTime, setToDateTime] = useState('');

  // Sync URL state to local component state
  useEffect(() => {
    if (filters.persona?.length) {
      setPersonaInput(filters.persona.join(', '));
    } else {
      setPersonaInput('');
    }

    if (filters.timeRange?.from) {
      // Convert ISO to datetime-local format
      const fromDate = new Date(filters.timeRange.from);
      setFromDateTime(fromDate.toISOString().slice(0, 16));
    } else {
      setFromDateTime('');
    }

    if (filters.timeRange?.to) {
      const toDate = new Date(filters.timeRange.to);
      setToDateTime(toDate.toISOString().slice(0, 16));
    } else {
      setToDateTime('');
    }
  }, [filters]);

  // Stage filter handlers with validation
  const toggleStage = useCallback((stage: StageName) => {
    // Ensure stage is valid before adding
    if (!Stage.options.includes(stage)) {
      console.warn(`Invalid stage attempted: ${stage}`);
      return;
    }

    const currentStages = filters.stage || [];
    const updatedStages = currentStages.includes(stage)
      ? currentStages.filter(s => s !== stage)
      : [...currentStages, stage];

    onFiltersChange({
      ...filters,
      stage: updatedStages.length > 0 ? updatedStages : undefined
    });
  }, [filters, onFiltersChange]);

  // Event type filter handlers
  const toggleEventType = useCallback((eventType: EventType) => {
    const currentTypes = filters.eventType || [];
    const updatedTypes = currentTypes.includes(eventType)
      ? currentTypes.filter(t => t !== eventType)
      : [...currentTypes, eventType];

    onFiltersChange({
      ...filters,
      eventType: updatedTypes.length > 0 ? updatedTypes : undefined
    });
  }, [filters, onFiltersChange]);

  // Persona filter handler
  const handlePersonaChange = useCallback((input: string) => {
    setPersonaInput(input);

    // Parse comma-separated values and update filters
    const personas = input
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0);

    onFiltersChange({
      ...filters,
      persona: personas.length > 0 ? personas : undefined
    });
  }, [filters, onFiltersChange]);

  // Time range handlers
  const handleFromDateChange = useCallback((value: string) => {
    setFromDateTime(value);

    const currentTimeRange = filters.timeRange;
    if (value) {
      const newTimeRange = {
        from: new Date(value).toISOString(),
        to: currentTimeRange?.to
      };
      onFiltersChange({
        ...filters,
        timeRange: newTimeRange
      });
    } else {
      if (currentTimeRange?.to) {
        onFiltersChange({
          ...filters,
          timeRange: { to: currentTimeRange.to }
        });
      } else {
        onFiltersChange({
          ...filters,
          timeRange: undefined
        });
      }
    }
  }, [filters, onFiltersChange]);

  const handleToDateChange = useCallback((value: string) => {
    setToDateTime(value);

    const currentTimeRange = filters.timeRange;
    if (value) {
      const newTimeRange = {
        from: currentTimeRange?.from,
        to: new Date(value).toISOString()
      };
      onFiltersChange({
        ...filters,
        timeRange: newTimeRange
      });
    } else {
      if (currentTimeRange?.from) {
        onFiltersChange({
          ...filters,
          timeRange: { from: currentTimeRange.from }
        });
      } else {
        onFiltersChange({
          ...filters,
          timeRange: undefined
        });
      }
    }
  }, [filters, onFiltersChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setPersonaInput('');
    setFromDateTime('');
    setToDateTime('');
    onFiltersChange({});
  }, [onFiltersChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.stage?.length ||
      filters.eventType?.length ||
      filters.persona?.length ||
      filters.timeRange?.from ||
      filters.timeRange?.to
    );
  }, [filters]);

  return (
    <div
      className={cn("space-y-4 p-4 rounded-lg border", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)',
      }}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3
          className="text-sm font-mono uppercase tracking-wider flex items-center gap-2"
          style={{
            fontFamily: 'var(--font-family-mono)',
            color: 'var(--accent)',
          }}
        >
          <Filter className="w-4 h-4" />
          Event Filters
        </h3>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-xs px-2 py-1 rounded border transition-colors hover:bg-opacity-20 focus:outline-none focus:ring-2"
            style={{
              color: 'var(--fg-muted)',
              borderColor: 'var(--border-subtle)',
              ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
              ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = 'var(--focus-ring)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid={`${testId}-clear-all`}
          >
            <X className="w-3 h-3 mr-1 inline" />
            Clear All
          </button>
        )}
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Stage Filter */}
        <div className="space-y-2">
          <label
            className="text-xs font-mono uppercase tracking-wider"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: 'var(--fg-secondary)',
            }}
          >
            Stages
          </label>
          <div className="flex flex-wrap gap-1">
            {STAGE_OPTIONS.map(({ value, label }) => {
              const isSelected = filters.stage?.includes(value) || false;
              return (
                <button
                  key={value}
                  onClick={() => toggleStage(value)}
                  className="px-2 py-1 text-xs rounded border transition-all focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: isSelected ? 'var(--accent)' : 'var(--bg-elevated)',
                    borderColor: isSelected ? 'var(--accent)' : 'var(--border-default)',
                    color: isSelected ? 'var(--bg-app)' : 'var(--fg-primary)',
                    fontFamily: 'var(--font-family-mono)',
                    ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  data-testid={`${testId}-stage-${value}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Event Type Filter */}
        <div className="space-y-2">
          <label
            className="text-xs font-mono uppercase tracking-wider"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: 'var(--fg-secondary)',
            }}
          >
            Event Types
          </label>
          <div className="flex flex-wrap gap-1">
            {EVENT_TYPE_OPTIONS.map(({ value, label }) => {
              const isSelected = filters.eventType?.includes(value) || false;
              return (
                <button
                  key={value}
                  onClick={() => toggleEventType(value)}
                  className="px-2 py-1 text-xs rounded border transition-all focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: isSelected ? 'var(--accent)' : 'var(--bg-elevated)',
                    borderColor: isSelected ? 'var(--accent)' : 'var(--border-default)',
                    color: isSelected ? 'var(--bg-app)' : 'var(--fg-primary)',
                    fontFamily: 'var(--font-family-mono)',
                    ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  data-testid={`${testId}-type-${value}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Persona Filter */}
        <div className="space-y-2">
          <label
            className="text-xs font-mono uppercase tracking-wider"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: 'var(--fg-secondary)',
            }}
            htmlFor={`${testId}-persona-input`}
          >
            Personas (comma-separated)
          </label>
          <div className="relative">
            <Search
              className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3"
              style={{ color: 'var(--fg-muted)' }}
            />
            <input
              id={`${testId}-persona-input`}
              type="text"
              placeholder="director, narrator, host..."
              value={personaInput}
              onChange={(e) => handlePersonaChange(e.target.value)}
              className="w-full pl-7 pr-2 py-2 text-xs rounded border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
                ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = 'var(--focus-ring)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid={`${testId}-persona-input`}
            />
          </div>
        </div>

        {/* Time Range Filter */}
        <div className="space-y-2">
          <label
            className="text-xs font-mono uppercase tracking-wider"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: 'var(--fg-secondary)',
            }}
          >
            Time Range
          </label>
          <div className="space-y-2">
            <div className="relative">
              <Calendar
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3"
                style={{ color: 'var(--fg-muted)' }}
              />
              <input
                type="datetime-local"
                placeholder="From"
                value={fromDateTime}
                onChange={(e) => handleFromDateChange(e.target.value)}
                className="w-full pl-7 pr-2 py-2 text-xs rounded border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`${testId}-from-date`}
              />
            </div>
            <div className="relative">
              <Calendar
                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3"
                style={{ color: 'var(--fg-muted)' }}
              />
              <input
                type="datetime-local"
                placeholder="To"
                value={toDateTime}
                onChange={(e) => handleToDateChange(e.target.value)}
                className="w-full pl-7 pr-2 py-2 text-xs rounded border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                  ['--focus-ring' as any]: '0 0 0 2px var(--border-focus)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--focus-ring)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid={`${testId}-to-date`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div
          className="text-xs p-2 rounded border"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          <div className="flex flex-wrap gap-2">
            {filters.stage?.length && (
              <span>Stages: {filters.stage.join(', ')}</span>
            )}
            {filters.eventType?.length && (
              <span>Types: {filters.eventType.join(', ')}</span>
            )}
            {filters.persona?.length && (
              <span>Personas: {filters.persona.join(', ')}</span>
            )}
            {filters.timeRange?.from && (
              <span>From: {new Date(filters.timeRange.from).toLocaleString()}</span>
            )}
            {filters.timeRange?.to && (
              <span>To: {new Date(filters.timeRange.to).toLocaleString()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}